import { InvoiceFileNameEnum } from '@sr-sdks/api-sdk-axios';
import { Button, Icon, SR_ICONS } from '@sr/ui-library';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { paidInvoicesParams, useInvoices } from '../api/payments/invoiceApi';
import { queryOptionsGetWorkspace } from '../api/workspaces/workspaceApi';
import { SEARCH_KEYS } from '../constant/SearchKeys';
import { useTablePaginationState } from '../hooks/useTablePaginationState';
import EmptyValueErrorLayout from '../pages/Workspace/EmptyValueErrorLayout';
import downloadApi from '../utils/api/downloadApi';
import { workspaceTypeIsAgent } from '../utils/helpers/common';
import { DEFAULT_INVOICE_SORT } from '../utils/helpers/sort';
import { toDisbursementSavedFileName } from '../utils/helpers/statementFiles';
import './AppTable.css';
import AppTableV2, { AppTableV2HeaderCell as TH } from './AppTableV2';
import { ServiceBadge } from './Badges/ServiceBadge';
import CurrencyFormatter from './CurrencyFormatter';
import DateFormatter from './DateFormatter';

type LoadingType = { invoiceId: string; isLoading: boolean };

/**
 * Its own sort param: staff WorkspaceDetails renders TitlesTable on the same
 * page, and a shared `sortBy` would send a title sort field to the invoices API.
 */
const PAYMENTS_SORT_BY_KEY = 'paymentsSortBy';

interface IPaymentsTableProps {
  isCollectionAccountMode?: boolean;
  isStaffView?: boolean;
  isWorkspaceSettingsPage?: boolean;
  titleId?: string;
  workspaceId?: string;
}

/**
 * Keyed on the workspace and title, so switching either remounts the table.
 * `keepPreviousData` would otherwise keep the last one's rows on screen,
 * looking like the new one's, until the next fetch lands.
 */
const PaymentsTable = (props: IPaymentsTableProps) => (
  <PaymentsTableContent
    key={`${props.workspaceId}:${props.titleId}`}
    {...props}
  />
);

const PaymentsTableContent = ({
  isCollectionAccountMode = false,
  isStaffView = false,
  isWorkspaceSettingsPage,
  titleId,
  workspaceId,
}: IPaymentsTableProps) => {
  const [isLoadingDownloadDocument, setLoading] = useState<LoadingType>();

  const { data: workspace } = useQuery(queryOptionsGetWorkspace(workspaceId));

  const { handlePageRequest, page, sortBy } = useTablePaginationState({
    defaultSortBy: DEFAULT_INVOICE_SORT,
    paginationKey: 'paymentsTablePage',
    searchKey: SEARCH_KEYS.PAYMENTS_SEARCH,
    sortByKey: PAYMENTS_SORT_BY_KEY,
  });

  const invoicesQuery = useInvoices(
    paidInvoicesParams({
      beneficiaryTitleId: titleId,
      beneficiaryWorkspaceId: workspaceId,
      page,
      pageSize: 10,
      sortBy,
    }),
    { enabled: !!workspaceId, placeholderData: keepPreviousData },
  );

  const invoices = invoicesQuery.data?.items ?? [];
  const paginationMeta = invoicesQuery.data?.meta ?? null;

  const handleDownloadDocument = async (invoiceId: string) => {
    setLoading({ invoiceId, isLoading: true });

    const fileName = InvoiceFileNameEnum.DisbursementStatementPdf;

    try {
      const paymentInvoiceBlob = await downloadApi.paymentStatement(
        fileName,
        invoiceId,
      );

      downloadApi.downloadFile(
        paymentInvoiceBlob,
        toDisbursementSavedFileName(invoiceId, fileName),
      );
    } catch (error) {
      // Do nothing because ErrorModal is already handed globally
    } finally {
      setLoading({ invoiceId, isLoading: false });
    }
  };

  if (
    isWorkspaceSettingsPage &&
    !isStaffView &&
    invoicesQuery.isSuccess &&
    invoices.length === 0
  ) {
    return <EmptyValueErrorLayout contentType="payment" />;
  }

  return (
    <>
      <AppTableV2
        id="payments-table"
        customEmptyTable={{
          description:
            'There are no payment records available for you to view at this time.',
          ...(!isStaffView &&
            isWorkspaceSettingsPage && { icon: SR_ICONS.folderOpen }),
          title: 'No payment records found',
        }}
        hasBottomNavigation={true}
        headerRowElement={
          <tr>
            <TH sortByKey={PAYMENTS_SORT_BY_KEY} sortByProperty={'invoiceDate'}>
              Date
            </TH>
            {!isCollectionAccountMode && <TH>Title</TH>}
            {workspaceTypeIsAgent(workspace) && <TH>Client</TH>}
            {!isCollectionAccountMode && <TH>Service</TH>}
            <TH
              className="!text-right"
              sortByKey={PAYMENTS_SORT_BY_KEY}
              sortByProperty={'amountNet'}
            >
              Amount
            </TH>
            <TH className="!text-center">Currency</TH>
            <TH hideLabel>Action</TH>
          </tr>
        }
        isFetchingPage={invoicesQuery.isFetching && !invoicesQuery.isLoading}
        isLoading={invoicesQuery.isLoading}
        pageRequestDeps="pageAndSort"
        paginationKey="paymentsTablePage"
        paginationMeta={paginationMeta}
        sortByKey={PAYMENTS_SORT_BY_KEY}
        onPageRequest={handlePageRequest}
      >
        {invoices.map((invoice) => (
          <tr key={invoice._id}>
            <td>
              <DateFormatter dateString={invoice.invoiceDate} />
            </td>
            {!isCollectionAccountMode && (
              <td>{invoice.beneficiaryTitleName || 'Unknown Title'}</td>
            )}
            {workspaceTypeIsAgent(workspace) && (
              <td>{invoice.beneficiaryClientName || 'Unknown Client'}</td>
            )}
            {!isCollectionAccountMode && (
              <td>
                <ServiceBadge serviceType={invoice.serviceType} />
              </td>
            )}
            <td className="!text-right">
              <CurrencyFormatter amount={invoice.amountNet} />
            </td>
            <td className="!text-center">{invoice.currency}</td>
            <td className="!text-center">
              <Button
                className="h-8 w-8"
                icon={<Icon icon="download" weight="fill" />}
                loading={
                  isLoadingDownloadDocument?.invoiceId === invoice._id &&
                  isLoadingDownloadDocument.isLoading
                }
                size="icon"
                onClick={() => handleDownloadDocument(invoice._id)}
              >
                <span className="sr-only">Download</span>
              </Button>
            </td>
          </tr>
        ))}
      </AppTableV2>
    </>
  );
};

export default PaymentsTable;
